import type {
  TaskView,
  WorkflowPhaseDef,
  WorkflowStepDef,
  WorkflowStepType,
  WorkflowSystemStep,
  UserSummary,
  WorkflowView,
} from '@/lib/api/generated/model';

/**
 * One step's cards, nested inside its phase's column. `kind` drives the
 * step-level badge (AI Step / Human Gate) and nothing else — notably not the
 * "+" control, which belongs to the workflow's own start step rather than to
 * any step that happens to be of `backlog` type.
 *
 * Named `kind` rather than `type` because `type` is a reserved-feeling word in
 * the view layer and this is the board's own vocabulary; it carries the
 * contract's `WorkflowStepType` values unchanged.
 *
 * `transitions` is included to support execution-status derivation (a step with
 * more than one onward path cannot advance on its own and needs a person to
 * choose); it mirrors the step definition's field when present.
 *
 * `systemRole` mirrors the value `buildBoard` stamps on the enclosing column,
 * so the derivation can recognise the terminal step from the step alone and
 * `deriveCardExecutionState` keeps its two-argument shape. Deliberately *not*
 * inferred from `kind`: a phase may declare its own step of `done` type, and
 * only the manufactured system column means "this task is finished".
 */
export interface BoardStepSection {
  stepId: string;
  label: string;
  kind: WorkflowStepType;
  tasks: TaskView[];
  /**
   * Who a Human Gate routes to, or `null` when it routes to nobody.
   *
   * Carried on the section rather than looked up per card because it is a
   * property of the *step*, not of any task standing on it — and the board is
   * polled every two seconds, so a lookup here would be repeated for every
   * column on every poll (spec 067 FR-027).
   */
  assignee?: UserSummary | null;
  transitions?: WorkflowStepDef['transitions'];
  systemRole?: 'start' | 'done';
  /**
   * The step's declared model and prompt, carried so the task detail can say
   * what the step a task is standing on will actually run.
   *
   * Both are properties of the workflow *as it stands now*, not of anything the
   * task has already done — which is precisely why they belong on the step
   * section and not on a recorded run. A run carries no prompt, so putting
   * today's text beside yesterday's run would attribute to it words it never
   * used. Optional because the two system steps declare neither.
   */
  llm?: string | null;
  prompt?: string | null;
  /**
   * The target workflow's key, for a `linked_workflow` step only (spec 101
   * US5). Carried the same way `llm`/`prompt` are — a property of the step
   * definition the task detail needs to resolve a display name — rather than
   * looked up again from the workflow, which the board already has.
   */
  workflow?: string | null;
}

/**
 * One top-level board column: a workflow phase, rendered once as a header,
 * with all of its steps — whatever their kind — as ordered sub-sections
 * beneath it. Every phase in a workflow becomes exactly one such column, and
 * so does each of the two system steps that bracket them (see `buildBoard`);
 * a phase that happens to contain a `backlog`- or `done`-kind step gets no
 * separate positional treatment for it.
 */
export interface BoardPhaseColumn {
  phaseId: string;
  label: string;
  steps: BoardStepSection[];
  /**
   * Set on the two columns the workflow's own system steps produce, and unset
   * on every author-declared phase. A phase may itself declare a step of
   * `backlog` or `done` *type*, so step kind cannot stand in for this — the
   * distinction is which side of the board the column was manufactured for.
   */
  systemRole?: 'start' | 'done';
}

/** The board projection's whole output. Not a stored entity. */
export interface Board {
  columns: BoardPhaseColumn[];
  /**
   * Tasks bucketed into Backlog because their stored `position` names no step
   * on this board (BR-002-18, FR-016) — typically because the workflow was
   * edited after the task was placed. Never silently dropped: `TaskCard` uses
   * this to render the "out of place" badge on exactly these cards.
   */
  orphanTaskIds: Set<string>;
}

export type BuildBoardResult =
  { ok: true; board: Board } | { ok: false; reason: 'no-workable-steps' };

interface OrderedStep {
  phaseId: string;
  phaseLabel: string;
  phaseDeclarationIndex: number;
  step: WorkflowStepDef;
  stepDeclarationIndex: number;
}

/**
 * Declared order *is* the order. The contract carries no `display_order` on
 * phases or steps — a workflow is authored as a YAML document, so the sequence
 * the author wrote them in is the sequence the service returns and the only
 * ordering signal there is.
 */
function flattenSteps(phases: WorkflowPhaseDef[]): OrderedStep[] {
  const flattened: OrderedStep[] = [];
  phases.forEach((phase, phaseDeclarationIndex) => {
    (phase.steps ?? []).forEach((step, stepDeclarationIndex) => {
      flattened.push({
        phaseId: phase.id,
        phaseLabel: phase.name,
        phaseDeclarationIndex,
        step,
        stepDeclarationIndex,
      });
    });
  });
  return flattened;
}

function comparePhaseThenStep(a: OrderedStep, b: OrderedStep): number {
  if (a.phaseDeclarationIndex !== b.phaseDeclarationIndex) {
    return a.phaseDeclarationIndex - b.phaseDeclarationIndex;
  }
  return a.stepDeclarationIndex - b.stepDeclarationIndex;
}

/** Deterministic within a step: by creation time, id breaks ties. Carries no product meaning. */
function compareTasks(a: TaskView, b: TaskView): number {
  if (a.created_at !== b.created_at) {
    return a.created_at < b.created_at ? -1 : 1;
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function toStepSection(
  ordered: OrderedStep,
  tasksByStep: Map<string, TaskView[]>,
): BoardStepSection {
  return {
    stepId: ordered.step.id,
    label: ordered.step.name,
    kind: ordered.step.type,
    assignee: ordered.step.assignee ?? null,
    tasks: (tasksByStep.get(ordered.step.id) ?? []).slice().sort(compareTasks),
    transitions: ordered.step.transitions,
    llm: ordered.step.llm,
    prompt: ordered.step.prompt,
    workflow: ordered.step.workflow,
  };
}

/**
 * A system step rendered as a phase of its own: `start_step` on the left,
 * `done_step` on the right. The phase id is derived from the step id, which is
 * `$`-prefixed and reserved by the service, so it can never collide with an
 * author-declared phase id.
 */
function systemColumn(
  step: WorkflowSystemStep,
  tasksByStep: Map<string, TaskView[]>,
  systemRole: 'start' | 'done',
): BoardPhaseColumn {
  return {
    phaseId: `phase:${step.id}`,
    label: step.name,
    systemRole,
    steps: [
      {
        stepId: step.id,
        label: step.name,
        kind: step.type,
        systemRole,
        tasks: (tasksByStep.get(step.id) ?? []).slice().sort(compareTasks),
      },
    ],
  };
}

/**
 * Projects a workflow's phases/steps and its tasks into board columns.
 *
 * Every board is bracketed by the workflow's two system steps, each rendered
 * as a phase of its own: `start_step` first, `done_step` last. They are not
 * members of any phase (the service synthesizes them outside the phase list),
 * and they are where a task begins and ends its life — so the board always has
 * a first and last column whatever the author declared.
 *
 * Between them, every declared phase becomes exactly one column, in declared
 * order, with all of its steps as ordered sub-sections. A step whose *type* is
 * `backlog` or `done` gets no special treatment there: inside a phase it is an
 * ordinary step like any other, distinct from the bracketing system steps
 * despite possibly sharing their name.
 *
 * A task whose `position` matches no step id on this board is placed in
 * Backlog (the workflow's `start_step`) rather than dropped — that only
 * arises when the workflow behind the board changed under a stored position,
 * and BR-002-18/FR-016 require it stay visible with an explanation rather
 * than vanish. Its id is recorded in `orphanTaskIds` so the card can carry
 * that explanation.
 *
 * Returns a discriminated result instead of throwing so one malformed
 * workflow cannot blank the board.
 */
export function buildBoard(
  workflow: WorkflowView,
  tasks: TaskView[],
): BuildBoardResult {
  const ordered = flattenSteps(workflow.phases).sort(comparePhaseThenStep);

  // No `no-backlog-step` check: `start_step` always exists, so every board has
  // somewhere for a task to start. Only the middle can be empty of real work.
  const hasWorkableStep = ordered.some(
    (o) => o.step.type === 'ai_step' || o.step.type === 'human_gate',
  );

  if (!hasWorkableStep) {
    return { ok: false, reason: 'no-workable-steps' };
  }

  const tasksByStep = new Map<string, TaskView[]>();
  const matchedStepIds = new Set([
    ...ordered.map((o) => o.step.id),
    workflow.start_step.id,
    workflow.done_step.id,
  ]);
  const orphanTaskIds = new Set<string>();

  tasks.forEach((task) => {
    const isOrphan = !matchedStepIds.has(task.position);
    const stepId = isOrphan ? workflow.start_step.id : task.position;
    if (isOrphan) orphanTaskIds.add(task.id);
    const bucket = tasksByStep.get(stepId) ?? [];
    bucket.push(task);
    tasksByStep.set(stepId, bucket);
  });

  const phaseColumns: BoardPhaseColumn[] = [];
  const columnIndexByPhase = new Map<string, number>();
  ordered.forEach((entry) => {
    let index = columnIndexByPhase.get(entry.phaseId);
    if (index === undefined) {
      index = phaseColumns.length;
      columnIndexByPhase.set(entry.phaseId, index);
      phaseColumns.push({
        phaseId: entry.phaseId,
        label: entry.phaseLabel,
        steps: [],
      });
    }
    phaseColumns[index].steps.push(toStepSection(entry, tasksByStep));
  });

  return {
    ok: true,
    board: {
      columns: [
        systemColumn(workflow.start_step, tasksByStep, 'start'),
        ...phaseColumns,
        systemColumn(workflow.done_step, tasksByStep, 'done'),
      ],
      orphanTaskIds,
    },
  };
}

/**
 * True when a step actually runs an AI turn (`ai_step`/`linked_workflow`) but
 * has no AI profile linked — the one condition that must block a task from
 * starting there (spec 122). A `human_gate` step is never blocked by this
 * check even when it also lacks a profile, since no AI turn runs there.
 */
export function isMissingAiProfile(step: {
  kind: WorkflowStepType;
  llm?: string | null;
}): boolean {
  return (
    (step.kind === 'ai_step' || step.kind === 'linked_workflow') && !step.llm
  );
}

/** Which phase column a step id belongs to, or undefined if it names no step on this board. */
export function findPhaseIdForStep(
  board: Board,
  stepId: string,
): string | undefined {
  return board.columns.find((column) =>
    column.steps.some((step) => step.stepId === stepId),
  )?.phaseId;
}
